import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSlug from "rehype-slug";
import {
  Alert,
  Box,
  Divider,
  Link as MuiLink,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { preprocessGitBook } from "./preprocessGitBook";

// Normalize a relative markdown link (e.g. "../workbench/README.md") to an
// absolute /docs route.
//
// currentFilePath is the actual markdown file path that's currently loaded
// (e.g. "policies-and-governance/custom-policies.md" or
// "workbench/README.md"). We use it — not the URL — to figure out the
// directory that relative links are anchored against, because a URL like
// /docs/policies-and-governance/custom-policies doesn't tell us whether it
// points at a leaf file (custom-policies.md) or a folder index
// (custom-policies/README.md).
function resolveDocsHref(currentFilePath, href) {
  if (!href) return href;

  // External or protocol-relative: leave alone.
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//")) return href;

  // Pure anchor on current page.
  if (href.startsWith("#")) return href;

  const [pathPart, anchor] = href.split("#");

  // Directory of the current file (relative to /docs root), with trailing /.
  const filePath = currentFilePath || "README.md";
  const lastSlash = filePath.lastIndexOf("/");
  const currentDir = lastSlash >= 0 ? filePath.slice(0, lastSlash + 1) : "";

  // Join and normalize ../ and ./ segments.
  const joined = currentDir + pathPart;
  const parts = [];
  for (const seg of joined.split("/")) {
    if (seg === "..") parts.pop();
    else if (seg !== "." && seg !== "") parts.push(seg);
  }
  let resolved = parts.join("/");

  // Strip trailing README.md and .md — the route is the human path.
  resolved = resolved
    .replace(/\.md$/i, "")
    .replace(/\/README$/i, "")
    .replace(/^README$/i, "");

  const route = resolved ? "/docs/" + resolved : "/docs";
  return anchor ? `${route}#${anchor}` : route;
}

// Walks ReactMarkdown children and returns the subset that matches a given
// data-doc attribute. Other children are returned separately so surrounding
// whitespace / stray nodes don't get lost.
function partitionTabs(children) {
  const tabs = [];
  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement(child) &&
      child.props &&
      child.props["data-doc"] === "tab"
    ) {
      tabs.push({
        title: child.props["data-title"] || "",
        node: child,
      });
    }
  });
  return tabs;
}

function DocTabs({ children }) {
  const tabs = useMemo(() => partitionTabs(children), [children]);
  const [value, setValue] = useState(0);
  const theme = useTheme();

  if (!tabs.length) return <Box>{children}</Box>;

  return (
    <Box
      sx={{
        my: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.3 : 0.18),
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.55 : 0.92),
      }}
    >
      <Tabs
        value={value}
        onChange={(_, v) => setValue(v)}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
        }}
      >
        {tabs.map((tab, i) => (
          <Tab key={i} label={tab.title} />
        ))}
      </Tabs>
      {tabs.map((tab, i) => (
        <Box
          key={i}
          role="tabpanel"
          hidden={value !== i}
          sx={{ px: { xs: 2, md: 3 }, py: 2 }}
        >
          {/* Render the matched tab's original children directly. */}
          {value === i ? tab.node.props.children : null}
        </Box>
      ))}
    </Box>
  );
}

function DocHint({ style, children }) {
  // Map GitBook styles to MUI Alert severities.
  const severity =
    style === "warning"
      ? "warning"
      : style === "danger" || style === "error"
      ? "error"
      : style === "success"
      ? "success"
      : "info";

  return (
    <Alert
      severity={severity}
      variant="outlined"
      sx={{
        my: 2.5,
        borderRadius: 2,
        "& .MuiAlert-message": { width: "100%" },
        "& p:last-child": { mb: 0 },
        "& p:first-of-type": { mt: 0 },
      }}
    >
      {children}
    </Alert>
  );
}

export default function DocsMarkdown({ markdown, currentFilePath }) {
  const theme = useTheme();
  const processed = useMemo(() => preprocessGitBook(markdown), [markdown]);

  const components = useMemo(() => {
    return {
      // Route special <div data-doc="..."> elements to custom components.
      div: ({ node, ...props }) => {
        const type = props["data-doc"];
        if (type === "hint") {
          return <DocHint style={props["data-style"]}>{props.children}</DocHint>;
        }
        if (type === "tabs") {
          return <DocTabs>{props.children}</DocTabs>;
        }
        if (type === "tab") {
          // Rendered inside DocTabs; when emitted bare, just render the body.
          return <>{props.children}</>;
        }
        return <div {...props} />;
      },

      a: ({ href, children, ...props }) => {
        const resolved = resolveDocsHref(currentFilePath, href);
        // Internal docs route → React Router link
        if (resolved && resolved.startsWith("/docs")) {
          return (
            <MuiLink component={RouterLink} to={resolved} underline="hover">
              {children}
            </MuiLink>
          );
        }
        // Anchor on current page
        if (resolved && resolved.startsWith("#")) {
          return (
            <MuiLink href={resolved} underline="hover">
              {children}
            </MuiLink>
          );
        }
        return (
          <MuiLink
            href={resolved}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            {...props}
          >
            {children}
          </MuiLink>
        );
      },

      h1: ({ children, ...props }) => (
        <Typography
          variant="h3"
          component="h1"
          sx={{ fontWeight: 900, letterSpacing: -0.5, mt: 0, mb: 2 }}
          {...props}
        >
          {children}
        </Typography>
      ),
      h2: ({ children, ...props }) => (
        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 900, mt: 5, mb: 1.5, scrollMarginTop: "80px" }}
          {...props}
        >
          {children}
        </Typography>
      ),
      h3: ({ children, ...props }) => (
        <Typography
          variant="h5"
          component="h3"
          sx={{ fontWeight: 800, mt: 4, mb: 1.25, scrollMarginTop: "80px" }}
          {...props}
        >
          {children}
        </Typography>
      ),
      h4: ({ children, ...props }) => (
        <Typography
          variant="h6"
          component="h4"
          sx={{ fontWeight: 800, mt: 3, mb: 1, scrollMarginTop: "80px" }}
          {...props}
        >
          {children}
        </Typography>
      ),

      p: ({ children }) => (
        <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2, fontSize: "1rem" }}>
          {children}
        </Typography>
      ),

      ul: ({ children }) => (
        <Box component="ul" sx={{ pl: 3, mb: 2, "& li": { mb: 0.75 } }}>
          {children}
        </Box>
      ),
      ol: ({ children }) => (
        <Box component="ol" sx={{ pl: 3, mb: 2, "& li": { mb: 0.75 } }}>
          {children}
        </Box>
      ),
      li: ({ children }) => (
        <Typography component="li" variant="body1" sx={{ lineHeight: 1.7 }}>
          {children}
        </Typography>
      ),

      blockquote: ({ children }) => (
        <Box
          sx={{
            my: 2,
            pl: 2,
            borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          {children}
        </Box>
      ),

      hr: () => <Divider sx={{ my: 3 }} />,

      code: ({ inline, className, children, ...props }) => {
        const content = String(children).replace(/\n$/, "");
        if (inline) {
          return (
            <Box
              component="code"
              sx={{
                px: 0.6,
                py: 0.1,
                borderRadius: 0.8,
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: "0.88em",
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1),
                color: theme.palette.mode === "dark" ? "#9fe5e1" : "#085e5a",
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.3 : 0.2),
              }}
            >
              {content}
            </Box>
          );
        }
        return (
          <Paper
            elevation={0}
            sx={{
              my: 2.5,
              overflow: "auto",
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.mode === "dark" ? "#0a1422" : "#0b1622",
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.3),
            }}
          >
            <Box
              component="pre"
              sx={{
                m: 0,
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: "0.86rem",
                lineHeight: 1.6,
                color: "#e6edf3",
              }}
            >
              <code className={className} {...props}>
                {content}
              </code>
            </Box>
          </Paper>
        );
      },

      table: ({ children }) => (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ my: 2.5, borderRadius: 2 }}
        >
          <Table size="small">{children}</Table>
        </TableContainer>
      ),
      thead: ({ children }) => <TableHead>{children}</TableHead>,
      tbody: ({ children }) => <TableBody>{children}</TableBody>,
      tr: ({ children }) => <TableRow>{children}</TableRow>,
      th: ({ children, style }) => (
        <TableCell sx={{ fontWeight: 800 }} align={style?.textAlign || "left"}>
          {children}
        </TableCell>
      ),
      td: ({ children, style }) => (
        <TableCell align={style?.textAlign || "left"}>{children}</TableCell>
      ),

      strong: ({ children }) => (
        <Box component="strong" sx={{ fontWeight: 800 }}>
          {children}
        </Box>
      ),

      img: ({ src, alt }) => (
        <Box
          component="img"
          src={src}
          alt={alt || ""}
          sx={{
            maxWidth: "100%",
            borderRadius: 2,
            display: "block",
            my: 2,
          }}
        />
      ),
    };
  }, [currentFilePath, theme]);

  return (
    <Box
      sx={{
        "& > *:first-of-type": { mt: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSlug]}
        components={components}
      >
        {processed}
      </ReactMarkdown>
    </Box>
  );
}
